import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search, LogIn } from "lucide-react";
import { NAV, ROUTES } from "./data";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-shadow ${scrolled ? "shadow-sm" : ""} bg-white/95 backdrop-blur-md border-b border-[#E5E5E5]`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-[70px] flex items-center justify-between gap-4">
        <Link to={ROUTES.home} data-testid="logo-link" className="flex flex-col leading-none shrink-0">
          <span className="font-heading font-extrabold text-xl sm:text-[22px] tracking-tight text-[#254369]">
            Wallstreet<span className="text-[#C9A961]"> Realty</span>
          </span>
          <span className="text-[10px] tracking-[0.22em] uppercase text-[#2D2D37]/50 mt-0.5">
            Real Estate & Mortgage
          </span>
        </Link>

        {/* Full nav from xl and up; hamburger below. 10 items need the room. */}
        <nav className="hidden xl:flex items-center gap-5">
          {NAV.map((n) => {
            const active = n.href === "/" ? pathname === "/" : pathname.startsWith(n.href);
            return (
              <Link
                key={n.label}
                to={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`ul-anim text-[14px] font-medium whitespace-nowrap transition-colors ${active ? "text-[#254369]" : "text-[#2D2D37]/80 hover:text-[#254369]"}`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <a
            href="https://app.wallstrealty.com/login"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="agent-login-link"
            className="hidden md:inline-flex items-center gap-1.5 text-[14px] font-medium text-[#2D2D37]/80 hover:text-[#254369] transition-colors ul-anim whitespace-nowrap"
          >
            <LogIn size={16} /> Agent Login
          </a>
          <Link
            to={ROUTES.search}
            data-testid="header-search-cta"
                        className="hidden sm:inline-flex items-center gap-2 bg-[#254369] text-white rounded-full px-5 py-2.5 font-medium text-[15px] hover:bg-[#2D2D37] transition-colors btn-shine whitespace-nowrap"
          >
            <Search size={16} /> Search Homes
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden text-[#254369] p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden border-t border-[#E5E5E5] bg-white px-4 py-4 space-y-1 max-h-[calc(100vh-70px)] overflow-y-auto"
        >
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.href}
              className="block py-2.5 text-[#2D2D37] font-medium border-b border-[#F5F5F5] last:border-0"
            >
              {n.label}
            </Link>
          ))}
          <Link
            to={ROUTES.search}
                        className="mt-3 inline-flex w-full justify-center items-center gap-2 bg-[#254369] text-white rounded-full px-5 py-3 font-medium"
          >
            <Search size={16} /> Search Homes
          </Link>
          <a
            href="https://app.wallstrealty.com/login"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="agent-login-link-mobile"
            className="mt-2 inline-flex w-full justify-center items-center gap-2 border-2 border-[#254369] text-[#254369] rounded-lg px-5 py-3 font-medium"
          >
            <LogIn size={16} /> Agent Login
          </a>
        </div>
      )}
    </header>
  );
};
